import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  // Constants
  static #VIDEO_SELECTORS = {
    video: 'video',
    iframe: 'iframe[src*="youtube"], iframe[src*="vimeo"]',
    iframeWithNocookie: 'iframe[src*="youtube"], iframe[src*="vimeo"], iframe[src*="youtube-nocookie"]',
    deferredMedia: 'deferred-media',
    playButton: '[ref="deferredMediaPlayButton"]'
  };

  static #VIDEO_CONFIG = {
    muted: true,
    controls: false
  };

  static #POLLING_CONFIG = {
    maxAttempts: 40,
    interval: 50,
    initialDelay: 100
  };

  static #VIDEO_TYPES = {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo'
  };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Add keyboard navigation for carousel
    if (this.presentation === 'carousel') {
      this.addEventListener('keydown', this.#handleKeydown, { signal });
    }

    // Listen for slideshow selection events to handle video autoplay
    this.refs.slideshow?.addEventListener('slideshow:select', this.#handleSlideSelect, { signal });

    // Listen for play button clicks to toggle video play/pause
    this.addEventListener('click', this.#handlePlayButtonClick, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Checks if the current brand is 'bk'.
   * @returns {boolean} True if brand is 'bk', false otherwise.
   */
  #isBakersBrand() {
    return this.dataset.brand === 'bk';
  }

  /**
   * Checks if a slide contains video content.
   * @param {HTMLElement} slide - The slide element to check.
   * @returns {boolean} True if slide contains video, false otherwise.
   */
  #isVideoSlide(slide) {
    return slide.classList.contains('has-video') ||
           slide.classList.contains('product-media-container--video') ||
           slide.classList.contains('product-media-container--external_video');
  }

  /**
   * Toggles video playback state.
   * @param {HTMLVideoElement} video - The video element to toggle.
   */
  #toggleVideoPlayback(video) {
    if (video.paused) {
      this.#playVideo(video);
    } else {
      this.#pauseVideo(video);
    }
  }

  /**
   * Toggles iframe video playback state.
   * @param {HTMLIFrameElement} iframe - The iframe element to toggle.
   */
  #toggleIframePlayback(iframe) {
    const isPlaying = iframe.dataset.playing === 'true';
    if (isPlaying) {
      this.#pauseIframeVideo(iframe);
      iframe.dataset.playing = 'false';
    } else {
      this.#playIframeVideo(iframe);
      iframe.dataset.playing = 'true';
    }
  }

  /**
   * Configures a video element with standard settings.
   * @param {HTMLVideoElement} video - The video element to configure.
   */
  #configureVideoElement(video) {
    video.muted = MediaGallery.#VIDEO_CONFIG.muted;
    video.controls = video.controls || MediaGallery.#VIDEO_CONFIG.controls;
  }

  /**
   * Plays a YouTube video.
   * @param {HTMLIFrameElement} iframe - The iframe element.
   * @param {string} url - The iframe URL.
   */
  #playYouTubeVideo(iframe, url) {
    // Ensure enablejsapi=1 for postMessage control
    if (!/enablejsapi=1/.test(url)) {
      const sep = url.includes('?') ? '&' : '?';
      iframe.src = `${url}${sep}enablejsapi=1`;
    }
    iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
  }

  /**
   * Plays a Vimeo video.
   * @param {HTMLIFrameElement} iframe - The iframe element.
   */
  #playVimeoVideo(iframe) {
    iframe.contentWindow?.postMessage({ method: 'play' }, '*');
  }

  /**
   * Pauses a YouTube video.
   * @param {HTMLIFrameElement} iframe - The iframe element.
   */
  #pauseYouTubeVideo(iframe) {
    iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  }

  /**
   * Pauses a Vimeo video.
   * @param {HTMLIFrameElement} iframe - The iframe element.
   */
  #pauseVimeoVideo(iframe) {
    iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
  }

  /**
   * Triggers deferred loading and waits for video to be rendered.
   * @param {HTMLElement} deferredMedia - The deferred media element.
   */
  #triggerDeferredLoading(deferredMedia) {
    const playButton = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.playButton);
    if (playButton && playButton instanceof HTMLElement) {
      playButton.click();
    }

    // Wait for the video to be rendered from the template
    let attempts = 0;
    const checkForVideo = () => {
      if (!this.isConnected || attempts++ >= MediaGallery.#POLLING_CONFIG.maxAttempts) return;
      
      const video = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.video);
      const iframe = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.iframe);
      
      if (video instanceof HTMLVideoElement) {
        this.#configureVideoElement(video);
        video.play().catch(() => {
          // Ignore autoplay errors
        });
      } else if (iframe instanceof HTMLIFrameElement) {
        this.#playIframeVideo(iframe);
      } else {
        // Video not yet rendered, check again
        setTimeout(checkForVideo, MediaGallery.#POLLING_CONFIG.interval);
      }
    };

    // Start checking after initial delay
    setTimeout(checkForVideo, MediaGallery.#POLLING_CONFIG.initialDelay);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles keyboard navigation for carousel.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeydown = (event) => {
    if (!this.slideshow || !this.#isBakersBrand()) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.slideshow.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.slideshow.next();
        break;
    }
  };

  /**
   * Handles slide selection to manage video autoplay.
   * @param {CustomEvent} event - The slideshow:select event.
   */
  #handleSlideSelect = (event) => {
    const { slide } = event.detail;
    if (!slide || !this.#isBakersBrand()) return;

    // Check if video autoplay is enabled
    if (this.dataset.videoAutoplay !== 'true') return;

    // Pause all videos in the gallery
    this.#pauseAllVideos();

    // Check if this is a video slide
    if (!this.#isVideoSlide(slide)) return;

    // Prefer deferred-media if present; otherwise play direct media if available
    const deferredMedia = slide.querySelector(MediaGallery.#VIDEO_SELECTORS.deferredMedia);
    if (deferredMedia) {
      this.#playDeferredVideo(deferredMedia);
    } else {
      const direct = slide.querySelector(MediaGallery.#VIDEO_SELECTORS.iframeWithNocookie);
      if (direct) this.#playVideo(direct);
    }
  };

  /**
   * Handles play button click to toggle video play/pause state.
   * @param {Event} event - The click event.
   */
  #handlePlayButtonClick = (event) => {
    if (!event.target || !(event.target instanceof Element)) return;
    
    const playButton = event.target.closest(MediaGallery.#VIDEO_SELECTORS.playButton);
    if (!playButton) return;

    const deferredMedia = playButton.closest(MediaGallery.#VIDEO_SELECTORS.deferredMedia);
    if (!deferredMedia) return;

    // Check if video is currently playing
    const video = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.video);
    const iframe = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.iframe);
    
    if (video instanceof HTMLVideoElement) {
      this.#toggleVideoPlayback(video);
    } else if (iframe instanceof HTMLIFrameElement) {
      this.#toggleIframePlayback(iframe);
    } else {
      // No video/iframe found, let default click behavior handle deferred loading
      return;
    }
  };

  /**
   * Pauses all videos in the media gallery.
   */
  #pauseAllVideos() {
    // Pause direct video elements
    const videos = this.querySelectorAll(`${MediaGallery.#VIDEO_SELECTORS.video}, ${MediaGallery.#VIDEO_SELECTORS.iframe}`);
    videos.forEach(video => {
      if (video instanceof HTMLVideoElement || video instanceof HTMLIFrameElement) {
        this.#pauseVideo(video);
      }
    });

    // Pause videos in deferred media components
    const deferredMediaElements = this.querySelectorAll(MediaGallery.#VIDEO_SELECTORS.deferredMedia);
    deferredMediaElements.forEach(deferredMedia => {
      if (deferredMedia instanceof HTMLElement) {
        this.#pauseDeferredVideo(deferredMedia);
      }
    });
  }

  /**
   * Plays a video with autoplay settings.
   * @param {HTMLVideoElement|HTMLIFrameElement} video - The video element to play.
   */
  #playVideo(video) {
    if (video instanceof HTMLVideoElement) {
      this.#configureVideoElement(video);
      video.play().catch(() => {
        // Ignore autoplay errors
      });
    } else if (video instanceof HTMLIFrameElement) {
      this.#playIframeVideo(video);
    }
  }

  /**
   * Pauses a video.
   * @param {HTMLVideoElement|HTMLIFrameElement} video - The video element to pause.
   */
  #pauseVideo(video) {
    if (video instanceof HTMLVideoElement) {
      video.pause();
    } else if (video instanceof HTMLIFrameElement) {
      this.#pauseIframeVideo(video);
    }
  }

  /**
   * Plays an iframe video (YouTube/Vimeo).
   * @param {HTMLIFrameElement} iframe - The iframe element.
   */
  #playIframeVideo(iframe) {
    try {
      const url = iframe.src || '';
      if (url.includes(MediaGallery.#VIDEO_TYPES.YOUTUBE)) {
        this.#playYouTubeVideo(iframe, url);
      } else if (url.includes(MediaGallery.#VIDEO_TYPES.VIMEO)) {
        this.#playVimeoVideo(iframe);
      }
      iframe.dataset.playing = 'true';
    } catch (error) {
      console.warn('Failed to play iframe video:', error);
    }
  }

  /**
   * Pauses an iframe video (YouTube/Vimeo).
   * @param {HTMLIFrameElement} iframe - The iframe element.
   */
  #pauseIframeVideo(iframe) {
    try {
      const url = iframe.src || '';
      if (url.includes(MediaGallery.#VIDEO_TYPES.YOUTUBE)) {
        this.#pauseYouTubeVideo(iframe);
      } else if (url.includes(MediaGallery.#VIDEO_TYPES.VIMEO)) {
        this.#pauseVimeoVideo(iframe);
      }
      iframe.dataset.playing = 'false';
    } catch (error) {
      console.warn('Failed to pause iframe video:', error);
    }
  }

  /**
   * Plays a video in a deferred media component.
   * @param {HTMLElement} deferredMedia - The deferred media element.
   */
  #playDeferredVideo(deferredMedia) {
    // Check if video already exists (from direct rendering)
    const existingVideo = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.video);
    const existingIframe = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.iframe);
    
    if (existingVideo instanceof HTMLVideoElement) {
      this.#configureVideoElement(existingVideo);
      existingVideo.play().catch(() => {
        // Ignore autoplay errors
      });
      return;
    }
    
    if (existingIframe instanceof HTMLIFrameElement) {
      this.#playIframeVideo(existingIframe);
      return;
    }

    // No existing video, trigger deferred loading
    this.#triggerDeferredLoading(deferredMedia);
  }

  /**
   * Pauses a video in a deferred media component.
   * @param {HTMLElement} deferredMedia - The deferred media element.
   */
  #pauseDeferredVideo(deferredMedia) {
    const video = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.video);
    const iframe = deferredMedia.querySelector(MediaGallery.#VIDEO_SELECTORS.iframe);
    
    if (video instanceof HTMLVideoElement) {
      video.pause();
    } else if (iframe instanceof HTMLIFrameElement) {
      this.#pauseIframeVideo(iframe);
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
